import { ButtonSet } from '@components/ButtonSet';
import {
  DirectoryServiceFolderRequest,
  DirectoryServiceFolderResponse,
} from '@core/@models/DirectoryServiceModel';
import { ApiService } from '@core/services/api.service';
import { Form, Input } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { finalize } from 'rxjs/operators';

const { Item } = Form;
const formItemLayout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 16 },
};

interface RootFolderFormProps {
  id?: React.Key;
  handleCancel: () => void;
  fetchData?: (param: { folderId: number; key: React.Key }) => void;
}

export const RootFolderForm: React.FC<RootFolderFormProps> = ({
  id,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const apiService = useMemo(() => new ApiService('/file-services/folder'), []);

  useEffect(() => {
    if (!id) return;
    getDataById();
  }, [id]);

  const getDataById = () => {
    apiService
      .getDataById<string, DirectoryServiceFolderResponse>(`${id}`)
      .subscribe({
        next: (result: DirectoryServiceFolderResponse) => {
          form.setFieldsValue(result);
        },
      });
  };

  const onFinish = (value: DirectoryServiceFolderRequest) => {
    const data = { ...value, id, parentId: 0 };

    const action$ = id
      ? apiService.updateData(data)
      : apiService.createData(data);
    setLoading(true);
    action$.pipe(finalize(() => setLoading(false))).subscribe({
      next: () => {
        props.fetchData && props.fetchData({ folderId: 0, key: 0 });
        props.handleCancel();
      },
    });
  };

  return (
    <Form
      form={form}
      name="folder-form"
      {...formItemLayout}
      onFinish={onFinish}
      autoComplete="off"
      preserve={false}
    >
      <Item
        name="name"
        label={t('Root Folder name')}
        rules={[{ required: true }, { max: 100 }]}
      >
        <Input placeholder={t('Root Folder name')} />
      </Item>
      <Item
        name="description"
        label={t('Root Folder description')}
        rules={[{ required: true }, { max: 200 }]}
      >
        <Input placeholder={t('Root Folder description')} />
      </Item>
      <Item
        name="fileLimitSize"
        label={t('File limit size')}
        rules={[
          { required: true },
          {
            pattern: new RegExp(/^[0-9]+$/),
            message: t('common:errorMessage.requiredInteger', {
              labelName: t('File limit size'),
            }),
          },
        ]}
      >
        <Input
          addonAfter="KB."
          maxLength={10}
          style={{ textAlign: 'right' }}
          placeholder={t('File limit size')}
        />
      </Item>
      <ButtonSet handleCancel={props.handleCancel} loading={loading} />
    </Form>
  );
};
